import React, {useMemo, useCallback, useState, useRef, useEffect} from 'react';
import {IconButton, MenuItem, Paper, Stack, Typography, Zoom} from '@mui/material';
import {
    DownloadOutlined,
    DescriptionOutlined,
    GetAppOutlined,
    ErrorOutline,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {useAppDispatch} from 'slices/hooks';
import {removeReport} from 'slices/reports';
import {DT_FORMATS, formatDate} from 'utils';
import styles from '../Header.module.scss';
import {LightTooltip} from '@ui/LightTooltip/LightTooltip';
import {useReportsManager} from '@ui/ReportModal/hooks/useReportsManager';
import {useTranslation} from 'react-i18next';
import {ReportDownloadManagerWrapper} from './Wrapper';
import {useDeviceInfo} from 'utils/hooks/useDeviceInfo';

export const ReportDownloadManager = () => {
    const {isMobile} = useDeviceInfo();

    const {t} = useTranslation(['npvAnalytics']);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const dispatch = useAppDispatch();
    const {reports, downloadReadyReport} = useReportsManager();

    const openMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const closeMenu = () => setAnchorEl(null);

    const hasInProgress = useMemo(
        () => reports.some((report) => report.status === 'IN_PROGRESS'),
        [reports],
    );
    const handleRemoveReport = useCallback(
        (reportId: string) => dispatch(removeReport(reportId)),
        [dispatch],
    );

    const [showReportsTooltip, setShowReportsTooltip] = useState(false);
    const previousReportsLength = useRef(reports.length);
    const tooltipTimeout = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const currentLength = reports.length;
        if (currentLength > previousReportsLength.current && !anchorEl) {
            setShowReportsTooltip(true);
            previousReportsLength.current = currentLength;

            if (tooltipTimeout.current) {
                clearTimeout(tooltipTimeout.current);
            }
            tooltipTimeout.current = setTimeout(() => {
                setShowReportsTooltip(false);
            }, 3000);
        }
    }, [reports, anchorEl]);

    return (
        <LightTooltip
            title={t('repDownloadManager.repClickToViewStatus')}
            open={showReportsTooltip}
            onClose={() => setShowReportsTooltip(false)}
            arrow
            TransitionComponent={Zoom}
            placement='bottom'
            followCursor={false}
            offset={[-5, 0]}
        >
            <div>
                {isMobile ? (
                    <MenuItem
                        onClick={openMenu}
                        className={styles.menuItem}
                        style={{borderTop: 'none'}}
                    >
                        <DownloadOutlined
                            className={hasInProgress ? styles.blinkingIcon : ''}
                            sx={{mr: 1}}
                        />
                        <Typography>{t('repDownloadManager.repGenerationAndUpload')}</Typography>
                    </MenuItem>
                ) : (
                    <IconButton
                        onClick={openMenu}
                        className={hasInProgress ? styles.blinkingIcon : ''}
                    >
                        <DownloadOutlined />
                    </IconButton>
                )}

                <ReportDownloadManagerWrapper
                    anchorEl={anchorEl}
                    onClose={closeMenu}
                >
                    {reports.length === 0 ? (
                        <MenuItem disabled>{t('repDownloadManager.noRequestedReports')}</MenuItem>
                    ) : (
                        reports.map((report) => (
                            <MenuItem
                                key={report.id}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    width: '100%',
                                }}
                            >
                                <Stack
                                    direction='row'
                                    alignItems='center'
                                    justifyContent='space-between'
                                    sx={{width: '100%'}}
                                >
                                    <DescriptionOutlined color='secondary' />
                                    <Stack sx={{flexGrow: 1}}>
                                        <Typography>
                                            {t('repDownloadManager.requestFromDate')}{' '}
                                            {formatDate(report.requestedAt, DT_FORMATS.default)}
                                        </Typography>
                                        <Typography variant='caption'>{report.title}</Typography>
                                        <Typography
                                            variant='body2'
                                            color={
                                                report.status === 'ERROR'
                                                    ? 'error'
                                                    : report.status === 'IN_PROGRESS'
                                                      ? 'primary'
                                                      : 'secondary'
                                            }
                                        >
                                            {report.status === 'IN_PROGRESS' &&
                                                `${t('repDownloadManager.repIsBeingGenerated')}...`}
                                            {report.status === 'DONE' &&
                                                t('repDownloadManager.repGenerated')}
                                            {report.status === 'ERROR' &&
                                                t('repDownloadManager.repGenerationErr')}
                                        </Typography>
                                    </Stack>

                                    {report.status === 'DONE' && (
                                        <IconButton
                                            onClick={() => downloadReadyReport(report.id)}
                                            size='small'
                                            color='success'
                                        >
                                            <GetAppOutlined />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        onClick={() => handleRemoveReport(report.id)}
                                        size='small'
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Stack>

                                {report.warnings?.length > 0 && (
                                    <Paper
                                        elevation={1}
                                        sx={{width: 370, p: 1, mt: 1}}
                                    >
                                        <Stack
                                            direction='row'
                                            alignItems='center'
                                            gap={1}
                                        >
                                            <ErrorOutline />
                                            <Stack
                                                minWidth={0}
                                                gap={0.5}
                                                height='100%'
                                            >
                                                {report.warnings.map((warning, idx) => (
                                                    <Typography
                                                        key={idx}
                                                        variant='caption'
                                                        color='warning'
                                                        component='div'
                                                        sx={{whiteSpace: 'normal'}}
                                                    >
                                                        {warning}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                )}
                            </MenuItem>
                        ))
                    )}
                </ReportDownloadManagerWrapper>
            </div>
        </LightTooltip>
    );
};
